// File: js/db/outbox-read.js
//
// What this device is still holding (audit H6).
//
// Today `sync.js:125-133` publishes a COUNT and nothing else, so a cashier who
// worked an offline morning reads « Recettes du jour : 0 FCFA » while holding a
// full cash drawer. Spec §9 is unambiguous: queued offline money MUST appear in
// totals, with the caveat « {n} vente(s) non encore synchronisée(s) sont incluses
// dans ce montant ».
//
// Read from the tables on the caller's tick, never from a cached counter. The sync
// engine's `status().outboxCount` refreshes at the END of a round and a write
// kicks a round without awaiting it, so a bare read of that field can report the
// PREVIOUS value in exactly the window that matters: the seconds between taking
// cash and printing the day's figure.
//
// The arithmetic is not reimplemented here. `foldMoney` from `money/` is the one
// place cash is summed, so an offline total and an online total can never disagree
// about the same events (audit H2 is precisely that disagreement).

import { foldMoney } from '../money/money-fold.js';

/**
 * How many records this device has not sent yet.
 * @param {import('dexie').Dexie} db
 * @returns {Promise<number>}
 */
export async function pendingCount(db) {
    return db.table('outbox').count();
}

/**
 * The XAF value of everything this device is still holding.
 *
 * Folded with `foldMoney`, so an unknown kind is excluded from the total and
 * surfaced rather than half-counted.
 *
 * @param {import('dexie').Dexie} db
 * @returns {Promise<number>} Net FCFA, integer.
 */
export async function pendingTotal(db) {
    const { net } = await pendingFold(db);
    return net;
}

/**
 * Count and value together, in one pass — what a tile needs to render both the
 * figure and its caveat without reading the table twice.
 *
 * @param {import('dexie').Dexie} db
 * @returns {Promise<{count:number, total:number, ignored:object[]}>}
 */
export async function pendingSummary(db) {
    const rows = await db.table('outbox').toArray();
    const fold = foldMoney(rows.map(r => r.payload).filter(Boolean));
    return { count: rows.length, total: fold.net, ignored: fold.ignored };
}

/**
 * The unsent money for ONE device-day — the figure the day close needs, and the
 * `unsynced_count` snapshot `close_day` records.
 *
 * The business date is read from the LEDGER mirror, not the outbox payload:
 * `business_date` is server-derived (invariant 9) and deliberately absent from the
 * §6 wire contract, so the outbox row does not carry it. The mirror row does.
 *
 * @param {import('dexie').Dexie} db
 * @param {string} deviceId
 * @param {string} businessDate `YYYY-MM-DD` (WAT).
 * @returns {Promise<{count:number, total:number}>}
 */
export async function pendingForDeviceDay(db, deviceId, businessDate) {
    const [queuedIds, dayRows] = await Promise.all([
        db.table('outbox').toCollection().primaryKeys(),
        db.table('ledger').where('[device_id+business_date]').equals([deviceId, businessDate]).toArray()
    ]);
    const queued = new Set(queuedIds);
    const unsent = dayRows.filter(r => queued.has(r.id));
    return { count: unsent.length, total: foldMoney(unsent).net };
}

/**
 * The ids this device has not sent, in drain order. Diagnostics and the conflict
 * screen; the drain itself reads the table directly.
 * @param {import('dexie').Dexie} db
 * @returns {Promise<string[]>}
 */
export async function pendingIds(db) {
    const rows = await db.table('outbox').orderBy('queued_at').toArray();
    return rows.map(r => r.id);
}

/**
 * Fold the whole outbox. Exposed because a caller that already needs `ignored[]`
 * should not have to fold twice.
 * @param {import('dexie').Dexie} db
 * @returns {Promise<import('../money/money-fold.js').MoneyFold>}
 */
export async function pendingFold(db) {
    const rows = await db.table('outbox').toArray();
    return foldMoney(rows.map(r => r.payload).filter(Boolean));
}
